import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("intellix_token")}` };
}

const emptyForm = { title: "", excerpt: "", content: "", author: "Intellix", category: "", read_time: "5 min" };

export default function AdminBlogForm() {
  const { id } = useParams();
  const isEdit = id && id !== "nouveau";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!isEdit);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("intellix_token")) { navigate("/admin/login"); return; }
    if (isEdit) {
      axios.get(`${API}/blog`).then(({ data }) => {
        const post = data.find((p) => p.id === id);
        if (post) {
          axios.get(`${API}/blog/${post.slug}`).then(({ data: full }) => {
            setForm({ title: full.title, excerpt: full.excerpt, content: full.content, author: full.author, category: full.category, read_time: full.read_time });
          });
        }
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/blog/${id}`, form, { headers: authHeaders() });
        toast.success("Article mis à jour");
      } else {
        await axios.post(`${API}/admin/blog`, form, { headers: authHeaders() });
        toast.success("Article créé");
      }
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center pt-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="admin-blog-form-page">
      <div className="mx-auto max-w-3xl">
        <Link to="/admin" className="group mb-8 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary" data-testid="admin-form-back-link">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> Retour au tableau de bord
        </Link>

        <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="admin-form-title">
          {isEdit ? "Modifier l'article" : "Nouvel article"}
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-blog-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="grid gap-2 text-sm font-medium sm:col-span-2">
              Titre
              <Input name="title" value={form.title} onChange={update} required minLength={3} placeholder="Titre de l'article" className="h-11 rounded-xl" data-testid="admin-form-title-input" />
            </label>
            <label className="grid gap-2 text-sm font-medium">
              Catégorie
              <Input name="category" value={form.category} onChange={update} required minLength={2} placeholder="Ex: Cybersécurité" className="h-11 rounded-xl" data-testid="admin-form-category-input" />
            </label>
            <label className="grid gap-2 text-sm font-medium">
              Temps de lecture
              <Input name="read_time" value={form.read_time} onChange={update} placeholder="Ex: 5 min" className="h-11 rounded-xl" data-testid="admin-form-readtime-input" />
            </label>
            <label className="grid gap-2 text-sm font-medium">
              Auteur
              <Input name="author" value={form.author} onChange={update} placeholder="Intellix" className="h-11 rounded-xl" data-testid="admin-form-author-input" />
            </label>
          </div>

          <label className="grid gap-2 text-sm font-medium">
            Extrait (résumé court)
            <Textarea name="excerpt" value={form.excerpt} onChange={update} required minLength={10} placeholder="Résumé qui apparaîtra dans la liste des articles..." className="min-h-24 rounded-xl" data-testid="admin-form-excerpt-input" />
          </label>

          <label className="grid gap-2 text-sm font-medium">
            <span>Contenu <span className="font-normal text-muted-foreground">(Markdown : ## titre, **gras**, - liste)</span></span>
            <Textarea name="content" value={form.content} onChange={update} required minLength={10} placeholder="## Introduction&#10;&#10;Votre contenu ici..." className="min-h-72 rounded-xl font-mono text-sm" data-testid="admin-form-content-input" />
          </label>

          <Button type="submit" disabled={saving} className="h-11 rounded-full px-8 font-semibold" data-testid="admin-form-submit-btn">
            {saving ? "Enregistrement..." : (isEdit ? "Mettre à jour" : "Publier l'article")} <Save className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
